/**
 * Block 2.2 (2026-05-26): Sick Leave API.
 */
import { api } from './client';
import type { Page } from './catalogs';


export type SickLeaveType =
  | 'GENERAL_ILLNESS' | 'CHILD_CARE' | 'MATERNITY' | 'WORK_ACCIDENT';


export interface SickLeave {
  employee_name?: string | null;
  id: string;
  organization_id: string;
  document_number: string;
  document_date: string;
  posted: boolean;
  posted_at: string | null;
  deletion_mark: boolean;
  comment: string | null;
  employee_id: string;
  sick_leave_type: SickLeaveType;
  start_date: string;
  end_date: string;
  days_count: number;
  certificate_number: string;
  certificate_date: string;
  diagnosis_code: string | null;
  insurance_stage_years: string;
  // ч.1118: страховий стаж за 12 місяців перед подією менший за 6 місяців
  // (з листка непрацездатності) — межа середньоденної, Порядок №1266 п.29.
  recent_stage_under_6m?: boolean;
  sick_leave_calculation_id: string | null;
  created_at: string;
  updated_at: string;
  external_source?: string | null;
}

export interface SickLeaveCreate {
  organization_id: string;
  document_number?: string | null;
  document_date: string;
  employee_id: string;
  sick_leave_type: SickLeaveType;
  start_date: string;
  end_date: string;
  certificate_number: string;
  certificate_date: string;
  diagnosis_code?: string | null;
  insurance_stage_years?: string;
  recent_stage_under_6m?: boolean;
  comment?: string | null;
}

export interface SickLeaveCalculation {
  // 0322: середня врахувала проведені мігровані (1С) нарахування → трикутник (лише preview).
  average_from_import?: boolean;
  // 0323: податки + net ОКРЕМО за роботодавця/Фонд (2 рядки + платіжка ЄСВ Фонду).
  employer_pdfo_amount?: string;
  employer_wz_amount?: string;
  employer_esv_amount?: string;
  employer_net_amount?: string;
  pfu_pdfo_amount?: string;
  pfu_wz_amount?: string;
  pfu_esv_amount?: string;
  pfu_net_amount?: string;
  id: string;
  organization_id: string;
  project_id?: string | null;
  document_number: string;
  document_date: string;
  posted: boolean;
  sick_leave_id: string;
  employee_id: string;
  sick_leave_type: SickLeaveType;
  start_date: string;
  end_date: string;
  days_count: number;
  insurance_stage_years: string;
  calc_period_start: string;
  calc_period_end: string;
  excluded_days_override?: number | null;
  effective_days_in_period: number;
  total_earnings_in_period: string;
  average_daily_wage_raw: string;
  average_daily_wage: string;
  daily_wage_capped_by_max: boolean;
  daily_wage_floored_by_min: boolean;
  // ч.1118: з чого взято середню і пояснення бухгалтеру (Порядок №1266).
  calc_basis?: string;
  calc_note?: string | null;
  recent_stage_under_6m?: boolean;
  applied_percentage_rate: string;
  effective_daily_wage: string;
  employer_days_count: number;
  employer_amount: string;
  pfu_days_count: number;
  pfu_amount: string;
  funding_source_marker: string;
  total_gross_amount: string;
  pdfo_rate: string;
  wz_rate: string;
  esv_rate: string;
  pdfo_amount: string;
  wz_amount: string;
  esv_amount: string;
  net_amount: string;
  expense_account_code: string;
  external_source?: string | null;
  monthly_parts?: SickMonthPart[];
}

export interface SickMonthPart {
  period_year: number;
  period_month: number;
  days_in_month: number;
  employer_days_in_month: number;
  pfu_days_in_month: number;
  employer_amount: string;
  pfu_amount: string;
  total_gross_amount: string;
  pdfo_amount: string;
  wz_amount: string;
  esv_amount: string;
}

export interface CalculateSickLeaveRequest {
  organization_id: string;
  sick_leave_id: string;
  project_id?: string | null;
  document_date?: string | null;
  pdfo_rate_override?: string | null;
  wz_rate_override?: string | null;
  esv_rate?: string;
  expense_account_code?: string;
  cost_item_id?: string | null;
  comment?: string | null;
  // TD-B-P5.1.4 (2026-06-01): ручний override excluded_days (≥0 або null).
  excluded_days_override?: number | null;
  // Ручний override середньоденної (перебив у формі).
  average_daily_override?: string | null;
}


const SL_BASE = '/documents/sick-leaves';
const SLC_BASE = '/documents/sick-leave-calculations';


export const sickLeave = {
  list: (params?: { organization_id?: string; employee_id?: string;
                    sick_leave_type?: SickLeaveType; posted?: boolean;
                    limit?: number; offset?: number;
                    sort_by?: string; sort_dir?: 'asc' | 'desc' }) =>
    api.get<Page<SickLeave>>(SL_BASE, { params }).then((r) => r.data),
  get: (id: string) =>
    api.get<SickLeave>(`${SL_BASE}/${id}`).then((r) => r.data),
  create: (p: SickLeaveCreate) =>
    api.post<SickLeave>(SL_BASE, p).then((r) => r.data),
  update: (id: string, p: Partial<SickLeaveCreate>) =>
    api.patch<SickLeave>(`${SL_BASE}/${id}`, p).then((r) => r.data),
  remove: (id: string) =>
    api.delete(`${SL_BASE}/${id}`).then(() => undefined),
  post: (id: string) =>
    api.post<SickLeave>(`${SL_BASE}/${id}/post`).then((r) => r.data),
  unpost: (id: string) =>
    api.post<SickLeave>(`${SL_BASE}/${id}/unpost`).then((r) => r.data),
};


export const sickLeaveCalculation = {
  list: (params?: { organization_id?: string; employee_id?: string;
                    posted?: boolean; limit?: number }) =>
    api.get<Page<SickLeaveCalculation>>(SLC_BASE, { params })
      .then((r) => r.data),
  get: (id: string) =>
    api.get<SickLeaveCalculation>(`${SLC_BASE}/${id}`).then((r) => r.data),
  preview: (p: CalculateSickLeaveRequest) =>
    api.post<SickLeaveCalculation>(`${SLC_BASE}/preview`, p)
      .then((r) => r.data),
  calculateAndPost: (p: CalculateSickLeaveRequest) =>
    api.post<SickLeaveCalculation>(`${SLC_BASE}/calculate-and-post`, p)
      .then((r) => r.data),
  // Зберегти DRAFT-розрахунок (БЕЗ проведення) — «Розрахувати» персистить чернетку.
  createDraft: (p: CalculateSickLeaveRequest) =>
    api.post<SickLeaveCalculation>(`${SLC_BASE}/draft`, p).then((r) => r.data),
  // Перерахувати існуючу чернетку (PATCH, тільки DRAFT).
  update: (id: string, p: {
    average_daily_override?: string | null;
    excluded_days_override?: number | null;
    pdfo_rate_override?: string | null;
    wz_rate_override?: string | null;
    esv_rate?: string | null;
    project_id?: string | null;
    expense_account_code?: string | null;
  }) =>
    api.patch<SickLeaveCalculation>(`${SLC_BASE}/${id}`, p).then((r) => r.data),
  unpost: (id: string) =>
    api.post<SickLeaveCalculation>(`${SLC_BASE}/${id}/unpost`).then((r) => r.data),
};
